import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, Lock, ChevronRight } from 'lucide-react';
import { auth } from '../firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from 'firebase/auth';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [authMethod, setAuthMethod] = useState<'email' | 'phone'>('email');
  
  // Form States
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    if (isOpen && authMethod === 'phone') {
      // Clean up any existing verifier before creating a new one
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (_) {}
        window.recaptchaVerifier = null;
      }

      // Small delay to ensure the DOM element exists
      const timer = setTimeout(() => {
        const container = document.getElementById('recaptcha-container');
        if (container) {
          window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            'size': 'invisible',
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, authMethod]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (_) {}
        window.recaptchaVerifier = null;
      }
      setOtpSent(false);
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
      onClose(); // Close modal on success
    } catch (err: any) {
      setError(err.message || 'Authentication failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const requestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;
      const appVerifier = window.recaptchaVerifier;
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      window.confirmationResult = confirmationResult;
      setOtpSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP.');
      // Reset reCAPTCHA if it fails so user can try again
      if (window.recaptchaVerifier) window.recaptchaVerifier.render().then((widgetId: any) => window.grecaptcha.reset(widgetId));
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await window.confirmationResult.confirm(otp);
      onClose(); // Close modal on success
    } catch (err: any) {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fade-in">
      <div className="relative w-full max-w-md bg-neutral-900 border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-8 pb-6 border-b border-white/10">
          <h2 className="text-2xl font-black uppercase tracking-widest text-center">
            {isLogin ? 'ACCESS_MAINFRAME' : 'INITIALIZE_USER'}
          </h2>
          <p className="text-center text-[10px] text-amber-400 uppercase tracking-widest mt-2 animate-pulse">
            SECURE CONNECTION ESTABLISHED
          </p>
        </div>

        {/* Body */}
        <div className="p-8">
          
          {/* Method Toggle */}
          <div className="flex bg-neutral-800 rounded-lg p-1 mb-8">
            <button
              onClick={() => { setAuthMethod('email'); setOtpSent(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${authMethod === 'email' ? 'bg-amber-600 text-white shadow-lg' : 'text-neutral-400 hover:text-white'}`}
            >
              <Mail className="w-3 h-3" /> Email
            </button>
            <button
              onClick={() => setAuthMethod('phone')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${authMethod === 'phone' ? 'bg-amber-600 text-white shadow-lg' : 'text-neutral-400 hover:text-white'}`}
            >
              <Phone className="w-3 h-3" /> Phone
            </button>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-xs font-mono text-center">
              ERR: {error}
            </div>
          )}

          {/* Email Form */}
          {authMethod === 'email' && (
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input 
                    type="email" 
                    required 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-neutral-950 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-sm font-mono focus:border-amber-500 outline-none transition-colors"
                    placeholder="user@system.com"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input 
                    type="password" 
                    required 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-neutral-950 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-sm font-mono focus:border-amber-500 outline-none transition-colors"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-white text-black py-4 rounded-xl font-black uppercase tracking-[0.2em] hover:bg-amber-600 hover:text-white transition-all mt-4 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? 'PROCESSING...' : isLogin ? 'LOGIN' : 'SIGN UP'}
                {!loading && <ChevronRight className="w-4 h-4" />}
              </button>
            </form>
          )}

          {/* Phone Form */}
          {authMethod === 'phone' && (
            <div>
              <div id="recaptcha-container"></div>
              
              {!otpSent ? (
                <form onSubmit={requestOTP} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                      <input 
                        type="tel" 
                        required 
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full bg-neutral-950 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-sm font-mono focus:border-amber-500 outline-none transition-colors"
                        placeholder="10-digit mobile number"
                      />
                    </div>
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-white text-black py-4 rounded-xl font-black uppercase tracking-[0.2em] hover:bg-amber-600 hover:text-white transition-all mt-4 disabled:opacity-50"
                  >
                    {loading ? 'SENDING...' : 'REQUEST OTP'}
                  </button>
                </form>
              ) : (
                <form onSubmit={verifyOTP} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Enter OTP</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                      <input 
                        type="text" 
                        required 
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        className="w-full bg-neutral-950 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-sm font-mono focus:border-amber-500 outline-none transition-colors tracking-[0.5em] text-center"
                        placeholder="••••••"
                        maxLength={6}
                      />
                    </div>
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-white text-black py-4 rounded-xl font-black uppercase tracking-[0.2em] hover:bg-amber-600 hover:text-white transition-all mt-4 disabled:opacity-50"
                  >
                    {loading ? 'VERIFYING...' : 'VERIFY & LOGIN'}
                  </button>
                  <button 
                    type="button"
                    onClick={() => setOtpSent(false)}
                    className="w-full text-center text-xs text-neutral-500 hover:text-white mt-4"
                  >
                    Change phone number
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Footer Toggle */}
        {authMethod === 'email' && (
          <div className="p-6 bg-neutral-950 border-t border-white/5 text-center">
            <button 
              onClick={() => setIsLogin(!isLogin)}
              className="text-xs text-neutral-400 hover:text-white uppercase tracking-widest"
            >
              {isLogin ? "DON'T HAVE AN ACCOUNT? SIGN UP" : "ALREADY IN THE GRID? LOGIN"}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default AuthModal;
// Add typings for the global window properties injected by Firebase Phone Auth
declare global {
  interface Window {
    recaptchaVerifier: any;
    confirmationResult: any;
    grecaptcha: any;
  }
}
